import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { carriers } from "../utils/carriers";
import Select from "react-select";

function TrackingPage() {
    const { orderId } = useParams();
    const [products, setProducts] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [form, setForm] = useState({
        trackingNumber: "",
        carrier: null,
        url: "",
    });
    const [statusMessage, setStatusMessage] = useState({ text: "", type: "" });

    const fetchOrderProducts = async () => {
        try {
            const response = await fetch(`/api/order/${orderId}`);
            const data = await response.json();
            const products = Object.values(data.metadata?.products || []);
            const trackedProductIds = data.trackedShipmentProducts || [];
            setProducts(products.filter(p => !trackedProductIds.includes(Number(p.id))));

            if ((data.metadata.products || []).length === 1) {
                setSelectedProducts([data.metadata.products[0].id]);
            } else {
                setSelectedProducts([]);
            }
        } catch (error) {
            console.error("Error fetching order products:", error);
            setStatusMessage({ text: "Error loading products.", type: "error" });
        }
    };

    useEffect(() => {
        fetchOrderProducts();
    }, [orderId]);

    const handleChange = (e) => {
        setForm({
            ...form,
            [e.target.name]: e.target.value,
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!selectedProducts.length || selectedProducts.length === 0) {
            setStatusMessage({ text: "Please select at least one product.", type: "error" });
            return;
        }

        try {
            const response = await fetch("/api/order/save_tracking", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    orderBsonId: orderId,
                    products: selectedProducts,
                    ...form,
                }),
            });

            const data = await response.json();

            if (data.response_code === 201) {
                setStatusMessage({ text: "Order saved successfully", type: "success" });
                // Refresh products after success
                fetchOrderProducts();
                setForm({ trackingNumber: "", carrier: null, url: "" });
                setSelectedProducts([]);
            } else {
                setStatusMessage({ text: "Failed to save order tracking.", type: "error" });
            }
        } catch (error) {
            console.error(error);
            setStatusMessage({ text: "Error saving tracking info.", type: "error" });
        }
    };

    return (
        <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
            <div className="bg-slate-900/80 backdrop-blur-xl border border-slate-600/30 rounded-2xl shadow-2xl p-8 w-full max-w-xl">
                <h1 className="text-2xl font-bold text-slate-50 mb-4 text-center">
                    Add Tracking Details
                </h1>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block mb-1 font-medium text-slate-200">
                            Products
                        </label>
                        <Select
                            options={products.map((p) => ({ label: p.name, value: p.id }))}
                            value={
                            selectedProducts.length > 0
                                   ? products
                                        .filter((p) => selectedProducts.includes(p.id))
                                        .map((p) => ({ label: p.name, value: p.id }))
                                   : null
                            }
                            onChange={(selectedOptions) =>
                                setSelectedProducts(selectedOptions ? selectedOptions.map((o) => o.value) : [])
                            }
                            isMulti
                            closeMenuOnSelect={false}
                            placeholder="Select Products"
                            className="text-slate-900"
                            required
                            noOptionsMessage={() =>
                                (selectedProducts.length > 0) && selectedProducts.length === products.length ? "All products selected" : "Tracking Details are already added for all products"
                            }
                        />
                    </div>

                    <div>
                        <label className="block mb-1 font-medium text-slate-200">
                            Tracking Number
                        </label>
                        <input
                            type="text"
                            name="trackingNumber"
                            placeholder="Enter tracking number"
                            value={form.trackingNumber}
                            onChange={handleChange}
                            className="w-full bg-slate-100 text-black border border-slate-600 rounded-md p-2 focus:ring-2 focus:outline-none"
                            required
                        />
                    </div>

                    <div>
                        <label className="block mb-1 font-medium text-slate-200">
                            Carrier
                        </label>
                        <Select
                            options={carriers.map((c) => ({ label: c.name, value: c.value }))}
                            value={form.carrier}
                            onChange={(selectedOption) =>
                                setForm({ ...form, carrier: selectedOption })
                            }
                            className="text-slate-900"
                            placeholder="Select Carrier"
                            isSearchable
                            isClearable
                            required
                        />
                    </div>

                    <div>
                        <label className="block mb-1 font-medium text-slate-200">
                            Tracking URL
                        </label>
                        <input
                            type="url"
                            name="url"
                            placeholder="Enter tracking URL"
                            value={form.url}
                            onChange={handleChange}
                            className="w-full bg-slate-100 text-black border border-slate-600 rounded-md p-2 focus:ring-2 focus:outline-none"
                            required
                        />
                    </div>

                    <button
                        type="submit"
                        className="w-full bg-[#c4fa00] hover:bg-[#c4fa00] transition-colors text-black font-semibold rounded-md p-2 mt-4"
                    >
                        Save Tracking
                    </button>
                </form>

                {statusMessage.text && (
                    <p
                        className={`mt-4 text-center font-medium ${
                            statusMessage.type === "success" ? "text-emerald-400" : "text-red-400"
                        }`}
                    >
                        {statusMessage.text}
                    </p>
                )}
            </div>
        </div>
    );
}

export default TrackingPage;
